import React, { useState, useEffect, useRef } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../components/ToastStyles/ToastStyles.css';
import { ImCross } from 'react-icons/im';
import { MdDelete } from 'react-icons/md';
import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io';
import { getFetchStockDetailTable, getFetchCatalogImage } from '../Redux/features/Stock/stockSlice';
import { useDispatch } from 'react-redux';
import { ACTUAL_IMAGE_URL, COMPRESSED_IMAGE_URL } from '../config/config';
import { getCartData, updateCart } from '../Redux/features/auth/authSlice';
import { IoIosStar, IoIosStarHalf, IoIosStarOutline } from 'react-icons/io';
import noImage from '../../src/components/images/noImage.png';

const ProductDetailV2 = ({
	ArticleCode,
	closeModal,
	ProductName,
	ProductId,
	Description,
	AllStock,
	ParentImagePath,
	Category,
	CategoryId,
	SubCategory,
	SubCategoryId,
	Rating,
}) => {
	const id = ArticleCode;
	const dispatch = useDispatch();

	const [ProductData, setProductData] = useState([]);
	const [catalogImages, setCatalogImages] = useState([]);
	const [activeImage, setActiveImage] = useState(null);
	const [colorGroups, setColorGroups] = useState([]);
	const [selectedItems, setSelectedItems] = useState([]);
	const [colorDropdownOpen, setColorDropdownOpen] = useState(false);
	const [selectedColors, setSelectedColors] = useState([]);
	const [colorSizeMap, setColorSizeMap] = useState({});
	const [cartItems, setCartItems] = useState([]);

	// const star = 3.5;
	const renderRatingStars = (rating) => {
		return Array.from({ length: 5 }, (_, index) => {
			let number = index + 0.5; // Half-star condition

			return (
				<span key={index}>
					{rating >= index + 1 ? (
						<IoIosStar className='text-[#DE7921] md:text-lg text-base lg:text-sm xl:text-lg' />
					) : rating >= number ? (
						<IoIosStarHalf className='text-[#DE7921] md:text-lg text-base lg:text-sm xl:text-lg' />
					) : (
						<IoIosStarOutline className='text-[#DE7921] md:text-lg text-base lg:text-sm xl:text-lg' />
					)}
				</span>
			);
		});
	};

	const dropdownRef = useRef(null);

	const handleColorChange = (color, size) => {
		const foundGroup = colorGroups.find((group) => group.color === color);
		const foundItem = foundGroup?.items.find((item) => item.Size === size);

		setSelectedItems((prevSelected) => {
			const exists = prevSelected.some((item) => item.color === color && item.size === size);

			if (exists) {
				return prevSelected.filter((item) => !(item.color === color && item.size === size));
			} else {
				return [
					...prevSelected,
					{
						color,
						colorId: foundGroup?.colorId, // Ensure colorId is added
						size,
						sizeId: foundItem?.sizeId, // Ensure sizeId is added
						qty: 1,
						MRP: foundItem?.MRP,
						DiscountMRP: foundItem?.DiscountMRP,
						Discount: foundItem?.Discount,
						DiscountType: foundItem?.DiscountType,
						Id: foundItem?.Id,
					},
				];
			}
		});
	};

	useEffect(() => {
		const uniqueColors = [...new Set(selectedItems.map((item) => item.color))];
		setSelectedColors(uniqueColors);
	}, [selectedItems]);

	// Handle quantity change for a specific color (product)
	const handleQtyChange = (index, newQty) => {
		let qty = parseInt(newQty, 10) || 1; // Ensure at least 1

		if (qty < 1) qty = 1; // Prevent 0 or negative values

		setCartItems((prevCartItems) =>
			prevCartItems.map((item, i) =>
				i === index
					? { ...item, Qty: qty, totalPrice: qty * (parseFloat(item.DiscountMRP) || 0) }
					: item
			)
		);
	};

	useEffect(() => {
		setCartItems((prevCartItems) => {
			return selectedItems.map((item) => {
				const existingItem = prevCartItems.find(
					(prevItem) => prevItem.Colors === item.color && prevItem.Size === item.size
				);

				return {
					Id: item.Id,
					Colors: item.color,
					ColorId: item.colorId, // Ensure colorId is included
					Size: item.size,
					SizeId: item.sizeId || existingItem?.SizeId, // Ensure sizeId is assigned
					Qty: existingItem ? existingItem.Qty : item.qty || 1,
					MRP: item?.MRP,
					DiscountMRP: item.DiscountMRP,
					Discount: item.Discount,
					DiscountType: item.DiscountType,
					totalPrice:
						(existingItem ? existingItem.Qty : item.qty || 1) * (parseFloat(item.DiscountMRP) || 0),
				};
			});
		});
	}, [selectedItems]);

	useEffect(() => {
		if (id) {
			dispatch(getFetchStockDetailTable(id)).then((data) => {
				if (data.payload && data.payload.StockDetail) {
					setProductData(data.payload.StockDetail);
					groupByColor(data.payload.StockDetail);
				} else {
					setProductData([]);
				}
			});
		}
	}, [dispatch, id]);

	const groupByColor = (stockDetails) => {
		const grouped = {};
		stockDetails.forEach((item) => {
			if (!grouped[item.Color]) {
				grouped[item.Color] = {
					color: item.Color,
					colorId: item.ColorId,
					ImagePath: item.ImagePath,
					items: [],
				};
			}
			grouped[item.Color].items.push({
				...item,
				sizeId: item.SizeId, // Ensure sizeId is added
			});
		});

		const groupedArray = Object.values(grouped);
		setColorGroups(groupedArray);

		if (groupedArray.length > 0) {
			preloadCatalogImages(groupedArray[0].items[0].Id);
		}
	};

	const preloadCatalogImages = (productId) => {
		if (!productId) return;
		dispatch(getFetchCatalogImage({ ProductId: productId })).then((response) => {
			const images = response?.payload?.catalogImages;
			if (Array.isArray(images) && images.length > 0) {
				const imagePaths = images.map((img) => img.CatalogueImagePath);
				setCatalogImages(imagePaths);
				setActiveImage(imagePaths[0]);
			} else {
				setCatalogImages([]);
				setActiveImage(null);
			}
		});
	};

	const handleImageClick = (Id) => {
		if (!Id) return;
		preloadCatalogImages(Id);
	};

	const handleAddToCart = async () => {
		const updatedProductData = {
			ArticleCode: ArticleCode,
			ProductName: ProductName,
			ProductId: ProductId,
			ParentImage: ParentImagePath,
			Category: Category,
			CategoryId: CategoryId,
			SubCategory: SubCategory,
			SubCategoryId: SubCategoryId,
			OrderDetails: cartItems.map((item) => ({
				ImagePathId: item.Id,
				color: item.Colors,
				colorId: item.ColorId,
				size: item.Size,
				sizeId: item.SizeId || item.sizeId,
				price: item.totalPrice,
				Qty: item.Qty,
				MRP: item.MRP,
				DiscountMRP: item.DiscountMRP,
				Discount: item.Discount,
				DiscountType: item.DiscountType,
			})),
		};

		// Retrieve current cart data
		let currentCart = localStorage.getItem('CartData');
		currentCart = currentCart ? JSON.parse(currentCart) : [];

		// Check if the product already exists in the cart
		const existingProductIndex = currentCart.findIndex(
			(item) => item.ArticleCode === updatedProductData.ArticleCode
		);

		if (existingProductIndex !== -1) {
			// Merge OrderDetails instead of replacing them
			updatedProductData.OrderDetails.forEach((newItem) => {
				const existingOrderIndex = currentCart[existingProductIndex].OrderDetails.findIndex(
					(existingItem) =>
						existingItem.color === newItem.color && existingItem.size === newItem.size
				);

				if (existingOrderIndex !== -1) {
					// If the same color & size exists, update the quantity
					currentCart[existingProductIndex].OrderDetails[existingOrderIndex].Qty += newItem.Qty;
				} else {
					// If it's a new color/size, add it to the OrderDetails array
					currentCart[existingProductIndex].OrderDetails.push(newItem);
				}
			});
		} else {
			// If it's a completely new product, add it to the cart
			currentCart.push(updatedProductData);
		}

		// Update local storage
		localStorage.setItem('CartData', JSON.stringify(currentCart));

		// Update Redux store
		dispatch(updateCart(currentCart));
		dispatch(getCartData());

		toast.success('Cart updated successfully');
	};

	const handleDelete = (itemIndex) => {
		const deletedItem = cartItems[itemIndex];

		// Remove from cartItems
		const updatedCartItems = cartItems.filter((_, index) => index !== itemIndex);
		setCartItems(updatedCartItems);

		// Remove from selectedItems
		setSelectedItems((prevSelected) =>
			prevSelected.filter(
				(item) => !(item.color === deletedItem.Colors && item.size === deletedItem.Size)
			)
		);

		// Remove from selectedColors
		setSelectedColors((prevColors) => prevColors.filter((color) => color !== deletedItem.Colors));

		// Remove from colorSizeMap
		setColorSizeMap(
			(prevMap) => {
				const updatedMap = { ...prevMap };
				delete updatedMap[deletedItem.Colors];
				return updatedMap;
			},
			[colorSizeMap]
		);
	};

	if (!ProductData || ProductData.length === 0) {
		return <div className='text-white'>Product not found</div>;
	}

	return (
		<div className=' bg-white rounded-md  relative shadow-md'>
			{catalogImages?.length > 0 && (
				<div className='absolute flex flex-col gap-2  lg:-left-9 -left-14 xl:-left-14 mt-0.5 max-md:hidden'>
					{catalogImages?.map((imgPath, index) => (
						<img
							key={index}
							src={`${ACTUAL_IMAGE_URL}/${imgPath}`}
							alt={`Catalog  ${index + 1}`}
							className={` w-[50px]  h-[65px] xl:w-[50px]  xl:h-[65px] lg:w-[30px] lg:h-[45px] object-cover rounded-md shadow-md cursor-pointer 
		      ${activeImage === imgPath ? 'border-2 border-red-500' : 'border-2 border-white'}`}
							onClick={() => setActiveImage(imgPath)}
						/>
					))}
				</div>
			)}
			<button
				onClick={closeModal}
				className=' z-10  absolute right-0 md:w-20 md:h-20 xl:w-20 xl:h-20 w-16 h-16 lg:h-16 lg:w-16  rounded-tr-md rounded-bl-full shadow-xl bg-red-500 hover:bg-red-600 transition-colors duration-300 text-center'>
				<ImCross className='text-white absolute lg:right-4 lg:top-4 xl:right-5 xl:top-5 md:right-5 right-4 md:text-lg text-base md:top-5 top-4 text-center' />
			</button>
			<div className=' overflow-y-auto custom-scrollbar'>
				<div className='flex md:flex-row gap-5 sm:p-5 p-3 flex-col max-md:h-[650px]  '>
					{/* image container  */}
					{activeImage && activeImage.length > 0 ? (
						<div className='w-full flex flex-col gap-2'>
							<img
								src={`${ACTUAL_IMAGE_URL}/${activeImage}`}
								alt='Main Product'
								className='lg:h-[410px] lg:w-[300px] xl:w-[425px] xl:h-[570px] md:h-[520px] md:w-[370px] w-[280px] max-[300px]:w-[200px] rounded-lg shadow-md object-cover'
							/>
						</div>
					) : ParentImagePath && ParentImagePath.length > 0 ? (
						<div className='w-full flex flex-col gap-2'>
							<img
								src={`${ACTUAL_IMAGE_URL}/${ParentImagePath}`}
								alt='Main Product'
								className='lg:h-[410px] lg:w-[300px] xl:w-[425px] xl:h-[570px] md:h-[520px] md:w-[370px] w-[280px] max-[300px]:w-[200px] rounded-lg shadow-md object-cover'
							/>
						</div>
					) : (
						<div className='w-full flex flex-col gap-2'>
							<img
								src={noImage}
								alt='Main Product'
								className='lg:h-[410px] lg:w-[300px] xl:w-[425px] xl:h-[570px] md:h-[520px] md:w-[370px] w-[280px] max-[300px]:w-[200px] rounded-lg shadow-md object-cover'
							/>
						</div>
					)}

					{catalogImages?.length > 0 && (
						<div className=' md:hidden flex flex-wrap gap-1.5  md:h-[520px] md:w-[380px]  w-[280px]  max-[300px]:w-[200px]  rounded-sm  max-sm:mb-5'>
							{catalogImages?.map((imgPath, index) => (
								<img
									key={index}
									src={`${ACTUAL_IMAGE_URL}/${imgPath} `}
									alt={`Catalog  ${index + 1}`}
									className={` w-[40px]  h-[50px]  object-cover rounded-md shadow-md cursor-pointer 
		      ${activeImage === imgPath ? 'border-2 border-red-500' : 'border-2 border-white'}`}
									onClick={() => setActiveImage(imgPath)}
								/>
							))}
						</div>
					)}
					{/* 2nd container  */}
					<div className='w-full relative'>
						<div className='xl:w-[410px] xl:h-[570px] lg:h-[410px] lg:w-[280px] md:h-[520px] md:w-[380px]  w-[280px]  max-[300px]:w-[200px] border-[1px] rounded-sm  border-[#e3e3e3] max-sm:mb-5'>
							<div className=' sm:px-5 sm:py-6 px-2 py-4 '>
								<p
									className={`font-bold lg:mb-1 xl:mb-3 md:mb-3 mb-1 text-gray-600 ${
										(ProductName?.length || 0) > 30
											? 'text-xl font-semibold'
											: 'md:text-2xl text-xl lg:text-lg'
									}`}>
									{ProductName}
								</p>
								{/* rating  */}
								<p className='md:text-base lg:text-xs xl:text-base text-sm lg:mb-1 xl:mb-3 md:mb-3 mb-1 text-red-500 font-semibold'>
									Stock Quantity: {AllStock}
								</p>
								<div className='flex flex-row gap-1 items-center'>
									<div className='md:text-base lg:text-xs xl:text-base text-sm font-semibold text-gray-600 mt-1'>
										{Number(Rating).toFixed(1)}
									</div>
									<div className='flex'>{renderRatingStars(Rating)}</div> {/* Call function here */}
								</div>
								{AllStock === 0 ? (
									<p className='md:text-base lg:text-xs xl:text-base text-sm lg:mb-3 mb-1 text-red-500 font-semibold'>
										Out of Stock
									</p>
								) : (
									<>
										{Description ? (
											<div className='md:overflow-y-auto custom-scrollbar pr-2'>
												<p className='md:text-[15px] xl:text-[15px] lg:text-sm text-xs md:h-16 lg:h-12 xl:h-16 mb-2 sm:text-sm leading-relaxed text-justify'>
													{/* <span className='font-semibold text-gray-600'>Description :</span> */}
													{Description}
												</p>
											</div>
										) : null}
										<div className='border-[1px] border-[#e3e3e3] lg:my-3 md:my-2 my-1   w-full' />

										<div>
											<div className='relative' ref={dropdownRef}>
												<div className='flex  overflow-x-auto flex-col  '>
													<label className='font-semibold mt-1 text-gray-600'>
														<span className=' md:text-base lg:text-xs xl:text-base text-sm '>
															Designs :
														</span>
													</label>
													<div
														className='flex items-center justify-between px-4 py-1 lg:py-0 border-2  border-gray-600 rounded-md cursor-pointer'
														onClick={() => setColorDropdownOpen(!colorDropdownOpen)}>
														<span className='text-gray-400'>Select Designs</span>
														{colorDropdownOpen ? <IoIosArrowUp /> : <IoIosArrowDown />}
													</div>
												</div>

												{colorDropdownOpen && (
													<div className='md:absolute top-full mt-1 left-0 border bg-white shadow-lg rounded-md z-10 '>
														<div className='p-2 max-h-44  overflow-y-auto custom-scrollbar'>
															{colorGroups.flatMap((group) =>
																group.items.map((item, idx) => (
																	<div
																		key={idx}
																		className='flex  items-center border-b border-gray-300 px-2 py-1 hover:bg-gray-100'>
																		<input
																			type='checkbox'
																			id={`${group.color}-${item.Size}`}
																			checked={selectedItems.some(
																				(selected) =>
																					selected.color === group.color &&
																					selected.size === item.Size
																			)}
																			onChange={() => handleColorChange(group.color, item.Size)}
																			className='mr-2'
																		/>
																		<label
																			htmlFor={`${group.color}-${item.Size}`}
																			className='text-gray-600 flex-grow '>
																			<tbody className=' max-lg:hidden max-xl:hidden'>
																				<tr className='flex justify-between items-center   text-sm md:text-sm'>
																					<td className='md:px-1  text-gray-700'>{group.color} </td>
																					<td className='md:px-1 text-gray-700'>{item.Size} </td>
																					<td className='md:px-1 text-gray-700'>{item.Qty}</td>
																					<td className='px-1 text-gray-900'>MRP</td>
																					<td className=' px-1 text-gray-700 font-medium'>
																						<span className='font-serif text-[16.5px]'>₹</span>
																						{Number(item?.DiscountMRP || 0).toLocaleString(
																							'en-IN',
																							{
																								minimumFractionDigits: 2,
																								maximumFractionDigits: 2,
																							}
																						)}
																					</td>
																					{item.Discount > 0 && (
																						<td className='px-2 text-gray-500'>
																							<span className='font-serif'>₹</span>
																							<span className='line-through text-sm'>
																								{item.MRP.toLocaleString('en-IN', {
																									minimumFractionDigits: 2,
																									maximumFractionDigits: 2,
																								})}
																							</span>
																						</td>
																					)}
																					{item.Discount > 0 && (
																						<td className='px-2 text-red-500 text-xs font-medium'>
																							(
																							{item.DiscountType === 'R' ? (
																								<>
																									<span className='font-serif'>₹</span>
																									{item.Discount}
																								</>
																							) : item.DiscountType === 'P' ? (
																								<>
																									{item.Discount}
																									<span>%</span>
																								</>
																							) : (
																								item.Discount
																							)}{' '}
																							OFF)
																						</td>
																					)}
																				</tr>
																			</tbody>
																			{/* till md   */}
																			<tbody className=' lg:hidden'>
																				<tr className='flex justify-between items-center   text-sm  '>
																					<td className='px-2 text-gray-700'>{group.color} </td>
																					<td className='px-2 text-gray-700'>{item.Size} </td>
																					<td className='px-2 text-gray-700'>{item.Qty}</td>
																					<td className=' px-2 text-gray-700 font-medium'>
																						MRP
																						<span className=' ml-1 font-serif text-[14.5px]'>
																							₹
																						</span>
																						{Number(item?.DiscountMRP || 0).toLocaleString(
																							'en-IN',
																							{
																								minimumFractionDigits: 2,
																								maximumFractionDigits: 2,
																							}
																						)}
																					</td>
																				</tr>
																				<tr className=' flex justify-between items-center   text-sm pr-2'>
																					{item.Discount > 0 && (
																						<td className='px-1 text-xs text-gray-500'>
																							<span className='font-serif'>₹</span>
																							<span className='line-through text-sm'>
																								{item.MRP.toLocaleString('en-IN', {
																									minimumFractionDigits: 2,
																									maximumFractionDigits: 2,
																								})}
																							</span>
																							{item.Discount > 0 && (
																								<span className='px-1 text-red-500 text-xs font-medium'>
																									(
																									{item.DiscountType === 'R' ? (
																										<>
																											<span className='font-serif'>₹</span>
																											{item.Discount}
																										</>
																									) : item.DiscountType === 'P' ? (
																										<>
																											{item.Discount}
																											<span>%</span>
																										</>
																									) : (
																										item.Discount
																									)}
																									OFF)
																								</span>
																							)}
																						</td>
																					)}
																				</tr>
																			</tbody>
																			{/* till lg  */}
																			<tbody className=' xl:hidden max-lg:hidden '>
																				<tr className='flex justify-between items-center   text-sm  '>
																					<td className='px-1 text-gray-700'>{group.color} </td>
																					<td className='px-1 text-gray-700'>{item.Size} </td>
																					<td className='px-1 text-gray-700'>{item.Qty}</td>
																					<td className=' px-1 text-gray-700 font-medium'>
																						MRP
																						<span className=' ml-1 font-serif text-[14.5px]'>
																							₹
																						</span>
																						{Number(item?.DiscountMRP || 0).toLocaleString(
																							'en-IN',
																							{
																								minimumFractionDigits: 2,
																								maximumFractionDigits: 2,
																							}
																						)}
																					</td>
																				</tr>
																				<tr className=' flex justify-between items-center   text-sm pr-2'>
																					{item.Discount > 0 && (
																						<td className='px-1 text-xs text-gray-500'>
																							<span className='font-serif'>₹</span>
																							<span className='line-through text-sm'>
																								{item.MRP.toLocaleString('en-IN', {
																									minimumFractionDigits: 2,
																									maximumFractionDigits: 2,
																								})}
																							</span>
																							{item.Discount > 0 && (
																								<span className='px-1 text-red-500 text-xs font-medium'>
																									(
																									{item.DiscountType === 'R' ? (
																										<>
																											<span className='font-serif'>₹</span>
																											{item.Discount}
																										</>
																									) : item.DiscountType === 'P' ? (
																										<>
																											{item.Discount}
																											<span>%</span>
																										</>
																									) : (
																										item.Discount
																									)}
																									OFF)
																								</span>
																							)}
																						</td>
																					)}
																				</tr>
																			</tbody>
																		</label>
																	</div>
																))
															)}
														</div>
													</div>
												)}
											</div>
										</div>

										<div className='border border-[#e3e3e3] lg:my-2 my-1 w-full ' />
										{catalogImages?.length > 0 && (
											<>
												<p className='font-semibold mt-1 md:text-base lg:text-xs xl:text-base text-sm   text-gray-600'>
													More Color :
												</p>
												<div className='flex flex-row gap-2 cursor-pointer md:mb-5 mb-2'>
													{colorGroups.map((group, index) => (
														<div
															key={index}
															className='group flex flex-col items-center cursor-pointer'
															onClick={() => handleImageClick(group.items[0].Id)}>
															<img
																src={`${COMPRESSED_IMAGE_URL}/${group.ImagePath}`}
																alt={`Color ${group.color}`}
																className='md:w-[55px] w-[40px] lg:w-[35px] xl:w-[48px] object-cover cursor-pointer rounded'
															/>
															{/* Color name appears only on hover */}
															<p className='text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200'>
																{group.color}
															</p>
														</div>
													))}
												</div>
											</>
										)}
									</>
								)}
							</div>
						</div>
					</div>

					{/* 3rd container  */}
					{selectedColors?.length > 0 && (
						<div className=' md:relative'>
							<div
								className={`xl:w-[400px] xl:h-[570px] lg:h-[410px] lg:w-[280px] md:h-[520px] md:w-[380px] w-[280px] max-[300px]:w-[200px] border-[1px] rounded-sm border-[#e3e3e3] max-sm:mb-5 transform transition-all duration-500 ease-in-out  ${selectedColors?.length > 0 ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-5 scale-95'}`}>
								<div className=' sm:px-5 lg:px-2 lg:py-4 xl:px-5 xl:py-6 sm:py-6 px-2 py-4'>
									{ProductData?.StockQty === 0 ? (
										<p className='md:text-base text-sm lg:mb-3 mb-1 text-red-500 font-semibold'>
											Out of Stock
										</p>
									) : (
										<>
											<div className='max-h-[440px] border border-gray-300 rounded-md overflow-y-auto custom-scrollbar'>
												<table className='min-w-full border-collapse table-auto text-black'>
													<thead className=' bg-white sticky top-0 '>
														<tr className='md:text-sm lg:text-xs xl:text-sm text-xs font-medium'>
															<th className='border-r md:px-1 px-0.5 py-2 text-center'>Color</th>
															<th className='border-r md:px-1 px-0.5 py-2 text-center'>Size</th>
															<th className='border-r md:px-1 px-0.5 py-2 text-center'>
																Quantity
																<br />
																<span className=' text-[10px] font-normal'>(add Qty)</span>
															</th>
															<th className='border-r md:px-1 py-2 text-center'>Total Price</th>
															<th className='border md:px-1 py-2 text-center'>Actions</th>
														</tr>
													</thead>
													<tbody>
														{cartItems.map((item, index) => (
															<tr key={index} className='md:text-sm lg:text-xs xl:text-sm text-xs'>
																<td className='border md:px-1 py-2 text-center'>{item?.Colors}</td>
																<td className='border text-center'>{item?.Size}</td>
																<td className='border text-center'>
																	<input
																		type='number'
																		value={item.Qty || 1}
																		onChange={(e) => handleQtyChange(index, e.target.value)}
																		className='md:w-20 w-[60px] lg:w-12 xl:w-20 text-center border border-gray-300 rounded py-[3px]'
																		min='1'
																	/>
																</td>
																<td className='border md:px-1 py-2 text-center'>
																	<span className='font-serif md:text-[16.5px] text-[12.5px] font-normal'>
																		₹
																	</span>
																	{Number(
																		item?.totalPrice || 1 * item?.DiscountMRP || 0
																	).toLocaleString('en-IN', {
																		minimumFractionDigits: 2,
																		maximumFractionDigits: 2,
																	})}
																</td>

																<td className='border px-1 py-2 text-center'>
																	<button
																		onClick={() => handleDelete(index)}
																		className='text-red-500 text-lg hover:text-red-700'>
																		<MdDelete />
																	</button>
																</td>
															</tr>
														))}
													</tbody>
												</table>
											</div>

											<div className=' md:absolute bottom-0 mb-4 lg:mb-2 xl:mb-4 max-md:mb-1 w-full md:pr-10 lg:pr-4 xl:pr-10'>
												<div className='border-[1px] border-[#e3e3e3] my-2  w-full mb-5 lg:mb-2 xl:mb-2 ' />
												<p className='md:text-base lg:text-xs xl:text-base text-sm  font-semibold md:mb-3 lg:mb-1 xl:mb-3 mb-2 '>
													Total Qty : {cartItems?.reduce((total, item) => total + item.Qty, 0)} ={' '}
													<span className=' font-serif text-[16.75px] lg:text-[13.75px] xl:text-[16.75px] font-normal '>
														₹
													</span>
													{/* {(ProductData.DiscountMRP * quantity).toFixed(2)} */}
													{cartItems
														?.reduce((total, item) => total + item.totalPrice, 0)
														.toLocaleString('en-IN', {
															minimumFractionDigits: 2,
															maximumFractionDigits: 2,
														})}
												</p>

												<div className='flex flex-row max-[300px]:flex-col justify-between lg:justify-center xl:justify-between items-center gap-5 '>
													<button
														onClick={() => handleAddToCart(ProductData, cartItems)}
														className='md:w-2/5 lg:w-full w-full max-md:text-[10px] text-sm lg:text-xs bg-red-500 text-white py-3 rounded-md font-semibold hover:bg-red-400'>
														Add To Cart
													</button>
													<button
														onClick={closeModal}
														className='md:w-2/5 lg:w-full w-full max-md:text-[10px] text-sm lg:text-xs bg-red-500 text-white py-3 rounded-md font-semibold hover:bg-red-400'>
														Go To Dashboard
													</button>
												</div>
											</div>
										</>
									)}
								</div>
							</div>
						</div>
					)}
				</div>
			</div>
			<ToastContainer
				position='top-right'
				autoClose={1000}
				hideProgressBar={true}
				closeOnClick
				pauseOnHover
				draggable
				pauseOnFocusLoss
				className=' mt-10 z-20'
			/>
		</div>
	);
};

export default ProductDetailV2;
